import React, { useState } from 'react';
import _ from 'lodash';
import cx from 'classnames';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';

import { pluralize, milisecondsToMinutes } from 'utils/utils';
import { Urls, CampaignTypes, CampaignStatus } from 'constants/constants';
import { pathTo, pushTo } from 'store/RouteState';
import { isCampaignPaused } from 'store/models/Campaign';
import { selectCurrentAccount, getAllTags } from 'store/models/Account';
import { defaultOverlay } from 'store/models/Overlay';

import { TextButton, IconButtonList, DeleteIconButton, EditButton, CircleAddButton, GraphIconButton, MoreIconButton, AddButton, AddIconButton, SmallButton } from 'components/Button';
import StatusDot from 'components/StatusDot';
import { SceneListRow } from 'components/SceneItem';
import { LinkIcon, CodeIcon, EditIcon, DeleteIcon, ChannelIcon, DownChevronIcon, UpChevronIcon, TagIcon } from 'components/Svg';
import { Menu, MenuItem } from 'components/Menu';
import { StatItemList, StatItemMoney, StatItemNumber, StatItemPercent } from 'components/Stats';
import Tooltip from 'components/Tooltip';
import { EmptyItemMargin } from 'components/EmptyItem';




const ChannelList = ({channels, displayCount = 4}) => {
  let display = channels
    |> _.take(#, displayCount)
    |> _.map(#, c => c.serviceName) 
    |> _.join(#, ', ')
    |> <span className="target-type"><ChannelIcon size="m" />{#}</span>;
  const totalCount = _.size(channels);
  const remaining = totalCount - displayCount;

  return totalCount > displayCount
    ? <>
        { display }
        <span className="more-channels"> ... <strong>+{remaining} Others</strong></span>
      </>
    : display
}



const tagListSelector = createSelector(
  state => selectCurrentAccount(state),
  (account) => ({ 
    tags: getAllTags(account) |> _.keyBy(#, 'tagId')
  }));

const TagList = ({tagIds, displayCount = 3}) => {
  const {tags} = useSelector(state => tagListSelector(state, { tagIds }))
  let display = tagIds 
    |> _.take(#, displayCount) 
    |> _.map(#, tagId => tags[tagId]?.name) 
    |> _.join(#, ', ')
    |> <span className="target-type"><TagIcon size="m" />{#}</span>
  const totalCount = _.size(tagIds);
  const remaining = totalCount - displayCount;
  return totalCount > displayCount
    ? <>
        { display }
        <span className="more-channels"> ... <strong>+{remaining} Others</strong></span>
      </>
    : display
}


export const CampaignItem = ({ campaign, scenes, links, overlay, className, size, onLinkCreate, onLinkUrls, onEdit, onDelete, ...props }) => {
  let showTargets = true; //!(_.size(campaign.channels) === 1 && (_.toLower(campaign?.name) === (campaign?.channels |> _.first |> #?.serviceName |> _.toLower)));f
  return (
    <Link
      className={cx("campaign-item", className, size, campaign?.status)}
      onClick={e => e.stopPropagation()}
      to={pathTo(Urls.CAMPAIGN_DETAILS_ROUTE, { campaignId: campaign._id })}
      {...props}>
      <div className="campaign-top">
        <div className="campaign-info">
          { campaign?.name && 
          <Tooltip tooltip={`Campaign ${campaign?.status}`}>
            <div className="campaign-name"> 
              <StatusDot status={campaign?.status} /> {campaign?.name}
            </div>
          </Tooltip>}
          { showTargets && 
          <div className="campaign-target">
            { campaign.type === CampaignTypes.ALL && <span className="target-type"><ChannelIcon size="s" /> All Channels</span>}
            { campaign.type === CampaignTypes.ALL && _.size(campaign.filters) > 0 &&
            <Tooltip tooltip={`All channels except ${_.size(campaign.filters)}`}>
              <div className="campaign-filters">
                - { _.size(campaign.filters) }
              </div>
            </Tooltip>}
            { campaign.type === CampaignTypes.CHANNELS && <ChannelList channels={campaign.channels} />}
            { campaign.type === CampaignTypes.TAGS && <TagList tagIds={campaign.tags} />}
          </div>}
          { overlay && 
            <div className="campaign-overlay">{overlay.name}</div>}
          { campaign.status === CampaignStatus.PAUSED &&
          <div className="campaign-status">{campaign?.status}</div>}
          
        </div>
        <IconButtonList className="campaign-actions" size="s">
          <Tooltip tooltip="Edit Campaign">
            <EditButton size="m" color="light" onClick={() => onEdit(campaign.brandId, campaign._id, campaign)} />
          </Tooltip>
          <Tooltip tooltip="Campaign Stats">
            <GraphIconButton size="m" onClick={() => pushTo(Urls.CAMPAIGN_DETAILS_ROUTE, { campaignId: campaign._id })} />
          </Tooltip>
        </IconButtonList>
      </div>
      <div className="campaign-bottom">
        <div className="campaign-scenes">
          <SceneListRow
            scenes={scenes}
            size="s"
            wrap={true}
            showEmpty={false} />
        </div>
        { _.size(links) > 0 &&
        <div className="campaign-links">
          <div className="links">
            { campaign.chatFrequency > 0 && 
              <div className="priority-text">Priority Delivery: <strong>{milisecondsToMinutes(campaign.chatFrequency)}</strong></div>}
            {_.map(links, link => (
              <div key={link._id} className="link-item">
                <div className="link-info">
                  <LinkIcon size="s" />
                  <a href={link.url} target="_blank">{link.name ?? link.url}</a>
                </div>
                <div className="link-actions">
                  <TextButton onClick={() => onLinkUrls?.(campaign._id, link._id)}><CodeIcon /> Short Links</TextButton>
                </div>
              </div>
            ))}
          </div>
        </div>}
        <div className="asset-actions">

        </div>
      </div>
    </Link>
  )}


// ====================
// LIST
// ====================
export const CampaignList = ({
  campaigns,
  linksByCampaign,
  scenesByCampaign,
  className,
  overlays,
  size,
  ...props }) => {
  return (
    <div
      className={cx("campaign-list", className, size)}>
      <div className="list-wrapper">
        {_.map(campaigns, campaign => (
          <CampaignItem
            key={campaign._id}
            campaign={campaign}
            scenes={scenesByCampaign?.[campaign._id]}
            links={linksByCampaign?.[campaign._id]}
            overlay={_.values(overlays)?.length > 1 
              ? _.find(overlays, { _id: campaign.overlayId })  ?? defaultOverlay(overlays)
              : null }
            size={size}
            {...props} />))}
      </div>
    </div>)
}




// ====================
// ITEM
// ====================

export const BrandItem = (props) => {
  let [ showPaused, setShowPaused ] = useState(false);
  
  let { 
    brand, campaigns, stats, scenesByCampaign, linksByCampaign, showCampaigns, overlays, className, size, 
    onShowCampaigns, onEditBrand, onDeleteBrand, onLinkCreate, onLinkUrls, onAddCampaign, onEditCampaign, onDeleteCampaign } = props;
  
  let [pausedCampaigns, runningCampaigns] = campaigns
    |> _.orderBy(#, 
        c => c.type === CampaignTypes.ALL
          ? 100000 
          : _.size(c.links) > 0
            ? 50000
            : c.priority, 'desc')
    |> _.partition(#, isCampaignPaused)

  const loading = _.isEmpty(stats);
  const isEmpty = !loading && _.size(campaigns) === 0;
  const isPaused = !_.isEmpty(campaigns) && _.every(campaigns, isCampaignPaused);

  const onDeleteBrandClick = () => onDeleteBrand(brand._id, brand);
  const onAddCampaignClick = () => onAddCampaign(brand._id, brand);
  const onEditBrandClick = () => onEditBrand(brand._id, brand);
  const onShowCampaignsToggle = e => {
    if(e.target?.href !== undefined)
      return;
    e.stopPropagation();
    onShowCampaigns(brand._id);
  }
  const onShowPausedToggle = e => {
    e.stopPropagation();
    setShowPaused(!showPaused);
  }

  return (
    <div className={cx("g-brand-item", className, size, {paused: isPaused})}>
      <div className="brand-top" onClick={onShowCampaignsToggle}>
        <div className="wrap-left">
          <div className="brand-info">
            <div className="brand-name">
              <span>{brand?.name}</span>
              <Menu
                target={props => <MoreIconButton size="m" {...props} />}>
                <MenuItem onClick={onEditBrandClick}><EditIcon /> Edit Brand Name</MenuItem>
                <MenuItem className="delete" onClick={onDeleteBrandClick}><DeleteIcon /> Delete Brand</MenuItem>
              </Menu>
            </div>
          </div>
          <StatItemList>
            <StatItemMoney label="Media Value" loading={loading} value={stats?.totalMediaValue} />
            <StatItemNumber label="Impressions" loading={loading} value={stats?.sceneViews} />
            <StatItemNumber label="Clicks" loading={loading} value={stats?.clicks} />
          </StatItemList>
        </div>
        <IconButtonList className="brand-actions">
          <Tooltip tooltip="New Campaign">
            <CircleAddButton size="s" onClick={onAddCampaignClick} />
          </Tooltip>
        </IconButtonList>
      </div>
      { isEmpty &&
        <EmptyItemMargin>
          <h3>Create a Campaign to hold your logos and links. Campaigns are targeted to only the channels you want.</h3>
          <AddButton onClick={onAddCampaignClick}>Add Campaign</AddButton>
        </EmptyItemMargin>}

      { _.size(campaigns) > 0 &&
      <div className="campaign-set" onClick={onShowCampaignsToggle}>
        <div className={cx("campaign-label", { open: showCampaigns })}>
          <div>
            <strong className="campaign-count">
              { _.size(runningCampaigns) ? _.size(runningCampaigns) : _.size(pausedCampaigns)}
            </strong>
            <span>
              {`${_.size(runningCampaigns) ? "Running" : "Paused"} ${pluralize('Campaign', _.size(runningCampaigns) ?? _.size(pausedCampaigns))}`}
            </span>
          </div>
          <DownChevronIcon />
        </div>
        { showCampaigns && <>
          {_.size(runningCampaigns) > 0 && <>
            <CampaignList 
              campaigns={runningCampaigns}
              scenesByCampaign={scenesByCampaign}
              linksByCampaign={linksByCampaign}
              overlays={overlays}
              onLinkCreate={onLinkCreate}
              onLinkUrls={onLinkUrls}
              onEdit={onEditCampaign}
              onDelete={onDeleteCampaign} />
              
            { _.size(pausedCampaigns) > 0 &&
            <div className="paused-set" onClick={onShowPausedToggle}>
              <div className="paused-top">
                <SmallButton className="paused-label" color="light">
                  {`+${_.size(pausedCampaigns)} Paused ${pluralize('Campaign', _.size(pausedCampaigns))}`}
                </SmallButton>
              </div>
              { showPaused &&
              <CampaignList 
                campaigns={pausedCampaigns}
                scenesByCampaign={scenesByCampaign}
                linksByCampaign={linksByCampaign}
                overlays={overlays}
                onLinkCreate={onLinkCreate}
                onLinkUrls={onLinkUrls}
                onEdit={onEditCampaign}
                onDelete={onDeleteCampaign} />}
            </div> }
          </>}
          { _.size(runningCampaigns) === 0 && _.size(pausedCampaigns) > 0 && 
          <CampaignList 
              campaigns={pausedCampaigns}
              scenesByCampaign={scenesByCampaign}
              linksByCampaign={linksByCampaign}
              overlays={overlays}
              onLinkCreate={onLinkCreate}
              onLinkUrls={onLinkUrls}
              onEdit={onEditCampaign}
              onDelete={onDeleteCampaign} />}
        </>}
      </div> }
    </div>)
}
export default BrandItem;



// ====================
// LIST
// ====================
export const BrandList = ({
  brands,
  brandStats,
  campaignsByBrand,
  linksByCampaign,
  scenesByCampaign,
  showCampaignsByBrand,
  overlays,
  header = true,
  className,
  size,
  ...props }) => {
  return (
    <div className={cx("g-brand-list", className, size)}>
      <div className="list-wrapper">
        {_.map(brands, brand => (
          <BrandItem
            key={brand._id}
            brand={brand}
            stats={brandStats?.[brand._id]}
            campaigns={campaignsByBrand?.[brand._id]}
            showCampaigns={showCampaignsByBrand?.[brand._id] ?? false}
            scenesByCampaign={scenesByCampaign}
            linksByCampaign={linksByCampaign}
            overlays={overlays}
            size={size}
            {...props} />))}
      </div>
    </div>)
}